import * as React from "react";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";

import {
  usePortfolioGateAccumulation,
  useAalPremiumGatePortfolio,
} from "../data";
import { GeographicGateBars, AalPremiumGateSections } from "./gateViews";
import styles from "./GateExposureDialog.module.css";

type PortfolioGateDialogProps = {
  visible: boolean;
  onHide: () => void;
};

type Section = {
  family: "wind" | "eq";
  title: string;
};

const SECTIONS: Section[] = [
  { family: "wind", title: "Wind / Named Storm" },
  { family: "eq", title: "Earthquake" },
];

// Book-wide gate accumulation: the geographic exposure gates (wind/EQ), then the
// per-peril AAL budgets and loss ratios. Both sum every bound and quoted deal.
export const PortfolioGateDialog: React.FC<PortfolioGateDialogProps> = ({
  visible,
  onHide,
}) => {
  const {
    data,
    isLoading: geoLoading,
    isError: geoError,
  } = usePortfolioGateAccumulation(visible);
  const {
    data: perilData,
    isLoading: perilLoading,
    isError: perilError,
  } = useAalPremiumGatePortfolio(visible);
  const isLoading = geoLoading || perilLoading;
  const isError = geoError || perilError;

  return (
    <Dialog
      header="Portfolio gate accumulation"
      visible={visible}
      onHide={onHide}
      style={{ width: "640px" }}
    >
      <div className={styles.container}>
        {isLoading && (
          <div className={styles.loading}>
            <ProgressSpinner />
          </div>
        )}
        {isError && (
          <Message
            severity="error"
            text="Could not load portfolio gate accumulation."
          />
        )}
        {!isLoading && !isError && (
          <>
            {data && (
              <>
                <p className={styles.intro}>
                  Each bar is the gate's full tracking base. Filled segments
                  show all bound and quoted deals across the book; the yellow
                  and red ticks mark the 70% and 85% escalation breakpoints.
                </p>
                {SECTIONS.map((section) => {
                  const bars = data.bars.filter(
                    (b) => b.peril_family === section.family
                  );
                  if (bars.length === 0) {
                    return null;
                  }
                  return (
                    <div key={section.family} className={styles.section}>
                      <div className={styles.sectionTitle}>{section.title}</div>
                      <GeographicGateBars
                        bars={bars}
                        breakpoints={data.breakpoints}
                        showCurrent={false}
                      />
                    </div>
                  );
                })}
              </>
            )}
            {perilData && (
              <AalPremiumGateSections
                bars={perilData.bars}
                capacityBreakpoints={perilData.capacity_breakpoints}
                perLayer={false}
              />
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
